<script setup>
import ModalBodyButtons from '@/components/front/combination/ModalBodyButtons.vue';
import ModalBodyCategoryList from '@/components/front/combination/ModalBodyCategoryList.vue';
import ModalBodyProductList from '@/components/front/combination/ModalBodyProductList.vue';
import ModalBodyProductItem from '@/components/front/combination/ModalBodyProductItem.vue';
import BsModal from '@/components/bootstrap/BsModal.vue';
import combinationStorage from '@/services/combination-storage';
import { defineOptions, ref, shallowRef } from 'vue';
import CombinationService from '@/services/combination-service';

defineOptions({
  name: 'CombinationProductPort'
});

const modalTitle = ref('');
const modalBodyComponents = shallowRef({
  'buttons': ModalBodyButtons,
  'categoryList': ModalBodyCategoryList,
  'productList': ModalBodyProductList,
  'productItem': ModalBodyProductItem
});

combinationStorage.categoryType = 'connector';

function handleModalState(title) {
  title === 'initial'
    ? modalTitle.value = '起始端'
    : modalTitle.value = '終端';

  combinationStorage.modalType = title;
  combinationStorage.modalCurrentComponent = 'buttons';
  combinationStorage.modalState = true;

  if (combinationStorage.initialItem || combinationStorage.terminalItem) {
    combinationStorage.getMappingConnector = true;
  } else {
    combinationStorage.getMappingConnector = false;
  }
}

function handleEditCard(type) {
  combinationStorage.modalType = type;
  combinationStorage.showBreadcrumb = false;
  combinationStorage.productId = combinationStorage[type + 'Item'].product.id;
  combinationStorage.modalCurrentComponent = 'productItem';
  combinationStorage.modalState = true;

  if (combinationStorage.initialItem && combinationStorage.terminalItem) {
    combinationStorage.getMappingConnector = true;
  } else {
    combinationStorage.getMappingConnector = false;
  }
}

const removeModalState = ref(false);
const removeTarget = ref(null);

function handleRemoveCard(type) {
  removeTarget.value = type;
  removeModalState.value = true;
}

function remove() {
  removeTarget.value === 'initial'
    ? combinationStorage.initialItem = null
    : combinationStorage.terminalItem = null;

  removeModalState.value = false;
  removeTarget.value = null;

  if (!combinationStorage.initialItem && combinationStorage.terminalItem) {
    combinationStorage.cables = {}
  }
}

function cancelRemove() {
  removeModalState.value = false;
  removeTarget.value = null;
}

function getImage(item) {
  return item.cover ? item.cover : item.mainVariant.cover;
}

function getVariantTitleText(title) {
  return title.replace(/\s*\/\s*/g, ' | ');
}

function closeModal() {
  combinationStorage.productId = null;
  combinationStorage.modalState = false
  combinationStorage.showBreadcrumb = true;
}
</script>

<template>
  <div class="l-product-port">
    <section class="l-product-port__order">
      <div class="container-fluid">
        <div class="row">
          <div class="col">
            <p class="l-product-port__tip text-primary text-center mx-auto mb-5">
              <i class="fa-regular fa-circle-info me-1"></i>如您僅需要購買加工線材，可以直接進入第二步
            </p>
          </div>
        </div>

        <div class="row justify-content-center mb-5">
          <div class="col-6 col-lg-3">
            <h2 class="h5 text-center mb-3">起始端</h2>
            <button type="button" class="c-combination-btn btn w-100"
              @click="handleModalState('initial')"
              v-if="!combinationStorage.initialItem">
              <i class="fa-solid fa-circle-plus h3 d-block mb-1"></i>
              點擊選擇
            </button>

            <div class="c-port-card card" v-else>
              <div class="ratio card-img-top ratio-1x1">
                <img :src="CombinationService.getVariantImage(combinationStorage.initialItem)"
                  :alt="combinationStorage.initialItem.title">
              </div>
              <div class="card-body">
                <h3 class="card-title mb-0 h5 position-relative">
                  {{ combinationStorage.initialItem.product.title }}
                </h3>
                <div class="card-text d-md-flex align-items-center gap-2 mb-1">
                  <p class="mb-0">
                    {{ combinationStorage.initialItem.product.model }}
                  </p>

                  <template v-if="combinationStorage.initialItem.sku">
                    <p class="mb-0">-</p>
                    <p class="mb-0">{{ combinationStorage.initialItem.sku }}</p>
                  </template>
                </div>

                <div v-if="!combinationStorage.initialItem.primary"
                  class="c-port-card__specifications text-dark mb-0"
                >
                  {{ CombinationService.getVariantTitleText(combinationStorage.initialItem.title) }}
                </div>

                <ul class="c-port-card__edit-btns d-flex gap-3 mb-0 ps-0">
                  <li>
                    <button type="button" class="btn fs-4 p-0" @click="handleEditCard('initial')">
                      <i class="fa-solid fa-pen"></i>
                    </button>
                  </li>
                  <li>
                    <button type="button" class="btn fs-4 p-0" @click="handleRemoveCard('initial')">
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="col-6 col-lg-3">
            <h2 class="h5 text-center mb-3">終端</h2>
            <button type="button" class="c-combination-btn btn w-100"
              @click="handleModalState('terminal')"
              v-if="!combinationStorage.terminalItem">
              <i class="fa-solid fa-circle-plus h3 d-block mb-1"></i>
              點擊選擇
            </button>

            <div class="c-port-card card" v-else>
              <div class="ratio card-img-top ratio-1x1">
                <img :src="CombinationService.getVariantImage(combinationStorage.terminalItem)"
                  :alt="combinationStorage.terminalItem.title">
              </div>
              <div class="card-body">
                <h3 class="card-title mb-0 h5 position-relative">
                  {{ combinationStorage.terminalItem.product.title }}
                </h3>
                <div class="card-text d-md-flex align-items-center gap-2 mb-1">
                  <p class="mb-0">
                    {{ combinationStorage.terminalItem.product.model }}
                  </p>

                  <template v-if="combinationStorage.terminalItem.sku">
                    <p class="mb-0">-</p>
                    <p class="mb-0">{{ combinationStorage.terminalItem.sku }}</p>
                  </template>
                </div>

                <div v-if="!combinationStorage.terminalItem.primary"
                  class="c-port-card__specifications text-dark mb-0"
                >
                  {{ CombinationService.getVariantTitleText(combinationStorage.terminalItem.title) }}
                </div>

                <ul class="c-port-card__edit-btns d-flex gap-2 mb-0 ps-0">
                  <li>
                    <button type="button" class="btn fs-4 p-0" @click="handleEditCard('terminal')">
                      <i class="fa-solid fa-pen"></i>
                    </button>
                  </li>
                  <li>
                    <button type="button" class="btn fs-4 p-0" @click="handleRemoveCard('terminal')">
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Modal -->
    <BsModal
      :open="combinationStorage.modalState"
      class="l-product-port__modal"
      :scrollable="true"
      @hidden="closeModal"
    >
      <template #header-element>
        <div class="modal-header">
          <h2 class="modal-title h5" id="productModalLabel">選擇{{ modalTitle }}</h2>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <component :is="modalBodyComponents[combinationStorage.modalCurrentComponent]"></component>
      </template>
    </BsModal>

    <BsModal
      :open="removeModalState"
      class="l-product-port__delete-modal"
      :centered="true"
      @hidden="removeModalState = false">
      <h3 class="h5 text-center mb-4">確認要刪除嗎？</h3>
      <div class="d-flex gap-2 justify-content-center">
        <button class="btn btn-primary" @click="remove">確認</button>
        <button class="btn btn-dark" @click="cancelRemove">取消</button>
      </div>
    </BsModal>
  </div>
</template>
